import { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Users, FileText, Send, Settings, Inbox, Menu, X, Home as HomeIcon } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { useAppConfig } from "@/lib/AppConfigContext";

const nav = [
  { to: "/", label: "Inicio", icon: HomeIcon, end: true, testid: "sidebar-nav-dashboard" },
  { to: "/proveedores", label: "Proveedores", icon: Users, testid: "sidebar-nav-suppliers" },
  { to: "/plantillas", label: "Plantillas", icon: FileText, testid: "sidebar-nav-templates" },
  { to: "/pedidos", label: "Pedidos", icon: Inbox, testid: "sidebar-nav-orders" },
  { to: "/configuracion", label: "Configuración", icon: Settings, testid: "sidebar-nav-smtp" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { config } = useAppConfig();
  const appLogo = config?.app_logo_base64 || "";

  // Close sidebar on route change (mobile)
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const LogoBadge = ({ size = "md" }) => {
    const s = size === "sm" ? "w-7 h-7" : "w-8 h-8";
    const inner = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";
    if (appLogo) {
      return (
        <div className={`${s} flex items-center justify-center rounded-sm overflow-hidden bg-white border border-[#E2E4E9]`}>
          <img src={appLogo} alt="logo" className="max-h-full max-w-full object-contain" data-testid="layout-app-logo" />
        </div>
      );
    }
    return (
      <div className={`${s} bg-[#002FA7] flex items-center justify-center rounded-sm`}>
        <Send className={`${inner} text-white`} strokeWidth={2} />
      </div>
    );
  };

  return (
    <div className="min-h-screen flex bg-[#F7F7F9]">
      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-[#E2E4E9] flex items-center justify-between px-4 h-14" data-testid="mobile-topbar">
        <button
          onClick={() => setOpen(true)}
          className="p-2 -ml-2 hover:bg-[#F7F7F9] rounded-sm"
          aria-label="Abrir menú"
          data-testid="mobile-menu-button"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <LogoBadge size="sm" />
          <span className="font-display font-bold text-sm">Pedidos</span>
        </div>
        <div className="w-9"></div>
      </header>

      {/* Backdrop on mobile when sidebar is open */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="md:hidden fixed inset-0 bg-black/40 z-40"
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`
          fixed md:static top-0 left-0 z-50 h-screen md:h-auto
          w-64 bg-white border-r border-[#E2E4E9] flex flex-col
          transform transition-transform duration-200 ease-out
          ${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0
        `}
      >
        <div className="px-6 py-6 border-b border-[#E2E4E9] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LogoBadge size="md" />
            <div>
              <h1 className="font-display font-bold text-base leading-none">Pedidos</h1>
              <p className="text-[10px] text-[#5A5D6E] uppercase tracking-[0.2em] mt-1">Proveedores</p>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="md:hidden p-1.5 hover:bg-[#F7F7F9] rounded-sm"
            aria-label="Cerrar menú"
            data-testid="sidebar-close-button"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm font-medium border-l-2 transition-colors duration-150 ${
                  isActive
                    ? "border-[#002FA7] bg-[#F7F7F9] text-[#002FA7]"
                    : "border-transparent text-[#5A5D6E] hover:bg-[#F7F7F9] hover:text-[#0D0E15]"
                }`
              }
            >
              <n.icon className="w-4 h-4" strokeWidth={1.75} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#E2E4E9]">
          <p className="text-[10px] text-[#5A5D6E] uppercase tracking-[0.2em]">Sistema interno</p>
          <p className="text-xs text-[#5A5D6E] mt-1">v1.0</p>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden pt-14 md:pt-0" data-testid="main-content">
        <Outlet />
      </main>
      <Toaster richColors position="top-right" />
    </div>
  );
}
